import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import Styles from "./CompanyDashboard.module.css";
import { ReactComponent as AddUserIcon } from "../../../assets/images/solid-user.svg";
import GroupIcon from "@mui/icons-material/Group";
import { ReactComponent as FileIcon } from "../../../assets/images/files.svg";
import { ReactComponent as MaskGroup } from "../../../assets/images/view.svg";
import { ReactComponent as Entity } from "../../../assets/images/user-add-orange.svg";
import { ReactComponent as EditIcon } from "../../../assets/images/editsolid.svg";
import { useDispatch, useSelector } from "react-redux";
import LoiTemplate from "../../Modals/CompanyAdmin/LoiTemplate/LoiTemplate";
import AddUserModal from "../../Modals/CompanyAdmin/AddUserModal/AddUserModal";
import {
  fetchConsumers,
  fetchDiscoms,
  fetchGenerators,
  fetchConsumer,
  fetchGeneratorDetails,
} from "../../../Redux/Slices/companyAdminSlice";
import {
  assignToEntity,
  changeConsumerActiveStatus,
  changeGeneratorActiveStatus,
} from "../../../Services/CompanyAdmin/AllApiAdmin";
import ActionModal from "../../Modals/CompanyAdmin/ActionModal/ActionModal";
import CompanyAdminConsumerForm from "../CompanyAdminConsumerForm/CompanyAdminConsumerForm";
import CompanyAdminGeneratorForm from "../CompanyAdminGeneratorForm/CompanyAdminGeneratorForm";
import ConfirmationModal from "../../../commons/ConfirmationModal/ConfirmationModal";
import ViewUsers from "../../Modals/CompanyAdmin/ViewUsers/ViewUsers";
import { getCompanyUsers } from "../../../Services/CompanyAdmin/GetApiAdmin";
import { ErrorMessage } from "../../../commons/Toast/ErrorMessage";
import { updateUser } from "../../../Services/CompanyAdmin/AllApiAdmin";
import { sideBarList } from "../../../Services/BuySell/Listing";
import { fetchSideBarList } from "../../../Redux/Slices/CapacitySlice";

const CompanyDashboard = () => {
  const dispatch = useDispatch();
  const [openLoiModal, setOpenLoiModal] = useState(false);
  const [openUserModal, setOpenUserModal] = useState(false);

  const [openUser, setOpenUser] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [openEntity, setOpenEntity] = useState(false);
  const [openAction, setOpenAction] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState(null);
  const [entityStatus, setEntityStatus] = useState({});
  const [selectedEntityStatus, setSelectedEntityStatus] = useState(true);
  const [selectedEntityType, setSelectedEntityType] = useState("consumer");
  const [userList, setUserList] = useState(null);
  const [assignUser, setAssignUser] = useState({
    status: false,
    role: "",
    id: "",
  });

  const { generators, consumers, discoms, consumerDetail, generatorDetail } =
    useSelector((state) => state.companyAdminSlice);
  const companyId = useSelector(
    (state) => state?.persistedReducer?.company?.companyId
  );

  const userData = useSelector((state) => state?.persistedReducer);

  useEffect(() => {
    dispatch(fetchGenerators());
    dispatch(fetchConsumers());
    dispatch(fetchDiscoms());
  }, [dispatch]);

  const entities = useMemo(
    () => [
      ...(consumers?.map((c) => ({
        ...c,
        id: c.consumerId,
        name: c.consumerName,
        accountType: "Consumer",
        entityType: c.entityType || "CONSUMER",
        original: c,
      })) || []),
      ...(generators?.map((g) => ({
        ...g,
        id: g.generatorId,
        name: g.generatorName,
        accountType: "Generator",
        entityType: g.entityType || "GENERATOR",
        original: g,
      })) || []),
      ...(discoms?.map((d) => ({
        ...d,
        id: d.discomId,
        name: d.discomName,
        accountType: "Discom",
        entityType: d.entityType || "DISCOM",
        original: d,
      })) || []),
    ],
    [consumers, generators, discoms]
  );

  const formatCount = (count) => {
    if (!count || count === 0) return "0";
    return count < 10 ? `0${count}` : count;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);

    const day = String(d.getDate()).padStart(2, "0");
    const month = d.toLocaleString("en-US", { month: "short" });
    const year = String(d.getFullYear()).slice(-2);

    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");

    return `${day}-${month}-${year}  ${hours}:${minutes}`;
  };

  const normalizedEntities = useMemo(
    () => [
      ...(generators?.map((g) => ({
        name: g.generatorName,
        id: g.generatorId,
        type: g.entityType || "GENERATOR",
        accountType: "Generator",
        original: g,
      })) || []),
      ...(consumers?.map((c) => ({
        name: c.consumerName,
        id: c.consumerId,
        type: c.entityType || "CONSUMER",
        accountType: "Consumer",
        original: c,
      })) || []),
      ...(discoms?.map((d) => ({
        name: d.discomName,
        id: d.discomId,
        type: d.entityType || "DISCOM",
        accountType: "Discom",
        original: d,
      })) || []),
    ],
    [generators, consumers, discoms]
  );

  const handleAddEntityClick = () => {
    setSelectedEntityType("consumer");
    setOpenEntity(true);
  };

  const handleCloseEntityCreation = () => {
    setOpenEntity(false);
    setSelectedEntityType("consumer");
    setEditMode(false);
    setSelectedEntityId(null);
  };

  const handleSwitchToGenerator = () => {
    setSelectedEntityType("generator");
  };

  const handleSwitchToConsumer = () => {
    setSelectedEntityType("consumer");
  };

  const handleEditEntity = async (entity) => {
    const entityType = entity.accountType.toLowerCase();

    setEditMode(true);
    setSelectedEntityType(entityType);

    if (entityType === "consumer") {
      setSelectedEntityId(entity.consumerId || entity.id);

      await dispatch(fetchConsumer(entity.consumerId || entity.id));
    } else if (entityType === "generator") {
      setSelectedEntityId(entity.generatorId || entity.id);

      await dispatch(fetchGeneratorDetails(entity.generatorId || entity.id));
    }

    setOpenEntity(true);
  };

  useEffect(() => {
    const initialStatus = {};
    entities.forEach((entity) => {
      const isActive =
        entity.original?.isActive ??
        entity.original?.status ??
        entity.isActive ??
        entity.status ??
        true;
      initialStatus[entity.id] = isActive;
    });
    setEntityStatus(initialStatus);
  }, [entities]);

  useEffect(() => {
    if (companyId) {
      handleFetchList();
    }
  }, []);

  const handleToggle = async (entity) => {
    // const entity = entities.find((e) => e.id === entityId);
    const currentStatus = entityStatus[entity.id];

    // const entityType = entity?.accountType?.toLowerCase();
    // if (entityType !== "consumer" && entityType !== "generator") {
    //   toast.error(
    //     `Cannot activate/deactivate ${
    //       entity?.accountType || "this entity type"
    //     }. Only consumers and generators can be toggled.`
    //   );
    //   return;
    // }

    // if (!entity) {
    //   console.error("Entity not found for ID:", entityId);
    //   toast.error("Entity not found.");
    //   return;
    // }

    setSelectedEntity(entity);
    setSelectedEntityStatus(currentStatus);
    setOpenDelete(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!selectedEntity) {
      console.error("No entity selected for status change");
      return;
    }

    try {
      const newStatus = selectedEntity?.isActive ? true : false;
      const entityType = selectedEntity.accountType?.toLowerCase();
      const entityId = selectedEntity.id;
      if (entityType === "consumer") {
        await changeConsumerActiveStatus(entityId, !newStatus);
      } else if (entityType === "generator") {
        await changeGeneratorActiveStatus(entityId, !newStatus);
      } else {
        toast.error(
          "Unsupported entity type. Only consumers and generators can be activated/deactivated."
        );
        return;
      }

      setEntityStatus((prev) => {
        const updated = {
          ...prev,
          [selectedEntity.id]: !newStatus,
        };
        return updated;
      });

      dispatch(fetchGenerators());
      dispatch(fetchConsumers());

      const statusText = !newStatus ? "activated" : "deactivated";
      toast.success(
        `${selectedEntity.accountType || "Entity"} ${statusText} successfully!`
      );
      dispatch(fetchSideBarList());

      setOpenDelete(false);
      setSelectedEntity(null);
    } catch (error) {
      console.error("Error updating entity status:", error);
      toast.error("Failed to update entity status. Please try again.");
    }
  };

  const handleEditUser = (data) => {
    setEditUser(data);
    setOpenUserModal(true);
  };

  const handleAlterUser = async ({ data, type }) => {
    const payload = {
      ...data,
      accountStatus: type,
    };
    let res = await updateUser(payload);
    if (res?.data?.code === 200) {
      handleFetchList();
    } else {
    }
  };

  const handleFetchList = async () => {
    let res = await getCompanyUsers(companyId);
    if (res?.data?.code === 200) {
      setUserList(res?.data?.data);
    } else {
      setUserList([]);
      ErrorMessage(res?.data?.error);
    }
  };
  return (
    <>
      {openLoiModal && (
        <LoiTemplate
          open={openLoiModal}
          handleClose={() => setOpenLoiModal(false)}
          heading={"Generate Intent"}
        />
      )}
      {openUserModal && (
        <AddUserModal
          open={openUserModal}
          handleClose={() => setOpenUserModal(false)}
          entities={normalizedEntities}
          userToEdit={editUser}
          handleFetchList={handleFetchList}
        />
      )}
      {openUser && (
        <ViewUsers
          open={openUser}
          handleClose={() => {
            setOpenUser(false);
            setAssignUser({
              status: false,
              role: "",
              id: "",
            });
          }}
          handleEditUser={handleEditUser}
          userList={userList}
          handleAlterUser={handleAlterUser}
          assign={assignUser}
        />
      )}
      {openEntity && selectedEntityType === "consumer" && (
        <div
          className={Styles.modalOverlay}
          onClick={handleCloseEntityCreation}
        >
          <div
            className={`${Styles.modalContent} ${Styles.consumerFormModal}`}
            onClick={(e) => e.stopPropagation()}
          >
            <CompanyAdminConsumerForm
              handleClose={handleCloseEntityCreation}
              onSwitchToGenerator={handleSwitchToGenerator}
              editMode={editMode}
              entityData={consumerDetail}
              entityId={selectedEntityId}
            />
          </div>
        </div>
      )}
      {openEntity && selectedEntityType === "generator" && (
        <div
          className={Styles.modalOverlay}
          onClick={handleCloseEntityCreation}
        >
          <div
            className={`${Styles.modalContent} ${Styles.generatorFormModal}`}
            onClick={(e) => e.stopPropagation()}
          >
            <CompanyAdminGeneratorForm
              handleClose={handleCloseEntityCreation}
              onSwitchToConsumer={handleSwitchToConsumer}
              editMode={editMode}
              entityData={generatorDetail}
              entityId={selectedEntityId}
            />
          </div>
        </div>
      )}

      {openAction && (
        <ActionModal
          open={openAction}
          handleClose={() => {
            setOpenAction(false);
            setSelectedEntity(null);
          }}
          selectedEntity={selectedEntity}
        />
      )}
      {openDelete && (
        <ConfirmationModal
          open={openDelete}
          handleClose={() => setOpenDelete(false)}
          handleSubmit={handleConfirmStatusChange}
          header={
            selectedEntity?.isActive
              ? `Deactivate ${selectedEntity?.accountType || "Entity"}`
              : `Activate ${selectedEntity?.accountType || "Entity"}`
          }
          data={
            selectedEntity?.isActive
              ? `Are you sure you want to deactivate this ${
                  selectedEntity?.accountType?.toLowerCase() || "entity"
                }?`
              : `Are you sure you want to activate this ${
                  selectedEntity?.accountType?.toLowerCase() || "entity"
                }?`
          }
        />
      )}

      <div className={Styles.companyAdminMainContainer}>
        <div className={Styles.companyDetails}>
          <div className={Styles.insideCompany}>
            {!userData?.roles?.some((role) =>
              ["TRADER", "ADMIN_TRADER"].includes(role)
            ) && (
              <>
                <div
                  className={Styles.companyUser}
                  onClick={handleAddEntityClick}
                >
                  <span>
                    <Entity style={{ width: "24px", height: "24px" }} />
                  </span>
                  <span>Add Entity</span>
                </div>

                <div
                  className={`${Styles.entitySection} ${Styles.companyUser}`}
                  onClick={() => setOpenUserModal(true)}
                >
                  <span>
                    <AddUserIcon style={{ width: "24px", height: "24px" }} />
                  </span>
                  <span style={{ color: "var(--ninth-color-text)" }}>
                    Add User
                  </span>
                </div>
                <div
                  className={`${Styles.entitySection} ${Styles.companyUser}`}
                  onClick={() => setOpenUser(true)}
                >
                  <span>
                    <GroupIcon
                      style={{
                        width: "28px",
                        height: "28px",
                        color: "#4267AF",
                      }}
                    />
                  </span>
                  <span style={{ color: "var(--ninth-color-text)" }}>
                    View User(s)
                  </span>
                </div>
                <div
                  className={`${Styles.template} ${Styles.companyUser}`}
                  onClick={() => setOpenLoiModal(true)}
                >
                  <span>
                    <FileIcon />
                  </span>
                  <span>Set Template</span>
                </div>
              </>
            )}
            {userData?.roles?.some((role) =>
              ["TRADER", "ADMIN_TRADER"].includes(role)
            ) && (
              <div className={Styles.companyDetailsItems}>
                <span>Total Entity</span>
                <span>{formatCount(entities?.length)}</span>
              </div>
            )}
            <div className={Styles.companyDetailsItems}>
              <span>Total Consumers</span>
              <span>{formatCount(consumers?.length)}</span>
            </div>
            <div className={Styles.companyDetailsItems}>
              <span>Total Generators</span>
              <span>{formatCount(generators?.length)}</span>
            </div>
            {discoms?.length > 0 ? (
              <div className={Styles.companyDetailsItems}>
                <span>Total Discoms</span>
                <span>{formatCount(discoms?.length)}</span>
              </div>
            ) : null}
          </div>
        </div>
        <div className={Styles.tableCompanyData}>
          <div className={Styles.tableWrapper}>
            <div
              className={`display_flex_row tableHeading ${Styles.tableCompanyHeader}`}
            >
              <div
                style={{
                  width: "14%",
                  textAlign: "left",
                  minWidth: "120px",
                }}
              >
                Entity Name
              </div>
              <div
                style={{
                  width: "20%",
                  textAlign: "center",
                  minWidth: "100px",
                }}
              >
                Entity Types
              </div>
              <div
                style={{
                  width: "25%",
                  textAlign: "center",
                  minWidth: "130px",
                }}
              >
                Created on
              </div>
              {!userData?.roles?.some((role) =>
                ["TRADER", "ADMIN_TRADER"].includes(role)
              ) && (
                <>
                  <div
                    style={{
                      width: "20%",
                      textAlign: "center",
                      minWidth: "110px",
                    }}
                  >
                    {/* Assigned User(s) */}
                  </div>
                  <div
                    style={{
                      width: "21%",
                      textAlign: "center",
                      minWidth: "140px",
                    }}
                  >
                    Actions
                  </div>
                </>
              )}
              {userData?.roles?.some((role) =>
                ["TRADER", "ADMIN_TRADER"].includes(role)
              ) && (
                <>
                  <div
                    style={{
                      width: "20%",
                      textAlign: "center",
                      minWidth: "110px",
                    }}
                  >
                    Number of Users
                  </div>
                  <div
                    style={{
                      width: "21%",
                      textAlign: "center",
                      minWidth: "140px",
                    }}
                  >
                    Actions
                  </div>
                </>
              )}
            </div>

            <div className={Styles.tableMainContainer}>
              {entities.map((entity, idx) => {
                const isDiscom = entity.accountType?.toLowerCase() === "discom";

                return (
                  <div
                    key={entity.id}
                    className={`display_flex_row ${Styles.tableDataColumn} ${
                      idx % 2 === 0 ? Styles.evenRow : Styles.oddRow
                    }`}
                  >
                    <div className={`display_flex_row ${Styles.tableRowData}`}>
                      <div
                        style={{
                          width: "14%",
                          textAlign: "left",
                          minWidth: "120px",
                          wordBreak: "break-word",
                          paddingRight: "8px",
                        }}
                      >
                        {entity.name || "-"}
                      </div>
                      <div
                        style={{
                          width: "20%",
                          textAlign: "center",
                          minWidth: "100px",
                        }}
                      >
                        {entity.accountType}
                      </div>
                      <div
                        style={{
                          width: "25%",
                          textAlign: "center",
                          minWidth: "130px",
                        }}
                      >
                        {formatDateTime(entity.createdAt)}
                      </div>

                      {!userData?.roles?.some((role) =>
                        ["TRADER", "ADMIN_TRADER"].includes(role)
                      ) && (
                        <div
                          className={Styles.userCont}
                          style={{
                            width: "20%",
                            textAlign: "center",
                            minWidth: "110px",
                          }}
                        >
                          <div style={{ fontSize: "10px" }}>
                            {/* {entity?.userCount ?? 0} */}
                          </div>
                          {/* <div
                            className={
                              entity.isActive === false || isDiscom
                                ? Styles.disabledBtn
                                : ""
                            }
                            onClick={
                              entity.isActive === false || isDiscom
                                ? undefined
                                : () => {
                                    setAssignUser({
                                      status: true,
                                      role: entity.accountType,
                                      id:
                                        entity?.consumerId ||
                                        entity?.generatorId,
                                    });
                                    setOpenUser(true);
                                  }
                            }
                            style={{
                              pointerEvents:
                                entity.isActive === false || isDiscom
                                  ? "none"
                                  : "auto",
                              opacity:
                                entity.isActive === false || isDiscom ? 0.5 : 1,
                              cursor:
                                entity.isActive === false || isDiscom
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                            title={
                              entity.isActive === false
                                ? "This entity is inactive"
                                : isDiscom
                                ? "Actions disabled for discom"
                                : ""
                            }
                          >
                            <MaskGroup
                              style={{ width: "16px", height: "12px" }}
                            />
                            View
                          </div> */}
                        </div>
                      )}

                      {!userData?.roles?.some((role) =>
                        ["TRADER", "ADMIN_TRADER"].includes(role)
                      ) && (
                        <div
                          className={Styles.actionBtns}
                          style={{ width: "21%", minWidth: "140px" }}
                        >
                          {/* Edit button */}
                          <div
                            className={
                              entity.isActive === false || isDiscom
                                ? Styles.disabledBtn
                                : ""
                            }
                            onClick={
                              entity.isActive === false || isDiscom
                                ? undefined
                                : () => handleEditEntity(entity)
                            }
                            style={{
                              pointerEvents:
                                entity.isActive === false || isDiscom
                                  ? "none"
                                  : "auto",
                              opacity:
                                entity.isActive === false || isDiscom ? 0.5 : 1,
                              cursor:
                                entity.isActive === false || isDiscom
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                            title={
                              entity.isActive === false
                                ? "This entity is inactive"
                                : isDiscom
                                ? "Edit disabled for discom"
                                : ""
                            }
                          >
                            <span>
                              <EditIcon />
                            </span>
                            Edit
                          </div>
                          <div></div>

                          <div>
                            {entity.accountType?.toLowerCase() === "consumer" ||
                            entity.accountType?.toLowerCase() ===
                              "generator" ? (
                              <label className={Styles.switch}>
                                <input
                                  type="checkbox"
                                  checked={entity.isActive === true}
                                  onChange={() => handleToggle(entity)}
                                />
                                <span className={Styles.slider}></span>
                              </label>
                            ) : (
                              <span style={{ opacity: 0.5, fontSize: "12px" }}>
                                N/A
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {userData?.roles?.some((role) =>
                        ["TRADER", "ADMIN_TRADER"].includes(role)
                      ) && (
                        <div
                          className={Styles.userCont}
                          style={{
                            width: "20%",
                            textAlign: "center",
                            minWidth: "110px",
                          }}
                        >
                          <div style={{ fontSize: "10px" }}>
                            {entity?.userCount ?? 0}
                          </div>
                        </div>
                      )}

                      {userData?.roles?.some((role) =>
                        ["TRADER", "ADMIN_TRADER"].includes(role)
                      ) && (
                        <div
                          className={Styles.userCont}
                          style={{
                            width: "20%",
                            textAlign: "center",
                            minWidth: "110px",
                          }}
                        >
                          <div style={{ fontSize: "10px" }}>
                            {/* {entity?.userCount ?? 0} */}
                          </div>
                          <div
                            className={
                              entity.isActive === false || isDiscom
                                ? Styles.disabledBtn
                                : ""
                            }
                            onClick={
                              entity.isActive === false || isDiscom
                                ? undefined
                                : () => {
                                    setAssignUser({
                                      status: true,
                                      role: entity.accountType,
                                      id:
                                        entity?.consumerId ||
                                        entity?.generatorId,
                                    });
                                    setOpenUser(true);
                                  }
                            }
                            style={{
                              pointerEvents:
                                entity.isActive === false || isDiscom
                                  ? "none"
                                  : "auto",
                              opacity:
                                entity.isActive === false || isDiscom ? 0.5 : 1,
                              cursor:
                                entity.isActive === false || isDiscom
                                  ? "not-allowed"
                                  : "pointer",
                              marginRight: "20px",
                            }}
                            title={
                              entity.isActive === false
                                ? "This entity is inactive"
                                : isDiscom
                                ? "Actions disabled for discom"
                                : ""
                            }
                          >
                            <MaskGroup
                              style={{ width: "16px", height: "12px" }}
                            />
                            View
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CompanyDashboard;
